import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { Practice, Routine } from '../types';
import { useEngine } from '../lib/useEngine';
import { advanceRun, beginRun, endRun, getRun, skipCurrent, subscribeRun, type Run } from '../lib/sessionStore';
import { getPractice } from '../lib/catalog';
import { currentStreak, loadHistory } from '../lib/storage';
import { formatClock, formatLength, poseTiming } from '../lib/timeline';
import { RoutineCard } from './RoutineCard';
import { dailyVerse } from '../lib/verses';
import { downloadReminder } from '../lib/reminder';
import { Icon } from '../ui/Icon';
import { SunGlow } from '../ui/SunGlow';
import { Sheet } from '../ui/parts';
import { StartPanel } from './StartPanel';
import styles from './player.module.css';

// ---- helpers ----

function useWide() {
  const query = '(min-width: 900px) and (orientation: landscape), (min-width: 1100px)';
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return wide;
}

function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        const l = await navigator.wakeLock.request('screen');
        if (cancelled) void l.release();
        else lock = l;
      } catch {
        /* unsupported or denied: the screen may sleep */
      }
    };
    void acquire();
    const onVisible = () => document.visibilityState === 'visible' && !lock && void acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void lock?.release();
    };
  }, [active]);
}

/** Hold for one second to end. Works with pointer and keyboard. */
const HoldToEnd = ({ onEnd }: { onEnd: () => void }) => {
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | null>(null);

  const start = () => {
    setHolding(true);
    timer.current = window.setTimeout(onEnd, 1000);
  };
  const stop = () => {
    setHolding(false);
    if (timer.current) window.clearTimeout(timer.current);
  };

  return (
    <button
      className={`${styles.hold} ${holding ? styles.holding : ''}`}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !holding && start()}
      onKeyUp={stop}
      onContextMenu={e => e.preventDefault()}
    >
      <span className={styles.holdFill} />
      <span className={styles.holdLabel}>{holding ? 'Keep holding to end' : 'Hold to end session'}</span>
    </button>
  );
};

// ---- the running player ----

const Running = ({ run, onExit }: { run: Run; onExit: () => void }) => {
  const engine = run.engine;
  const practice = run.queue[run.index];
  const chained = run.queue.length > 1;
  const snap = useEngine(engine);
  const settings = engine.getSettings();
  const wide = useWide();
  const [more, setMore] = useState(false);
  const [list, setList] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const [zoomed, setZoomed] = useState(false);
  const lastSegment = useRef(-1);

  const isPoses = practice.steps.some(s => s.kind === 'pose');
  const isTrack = practice.steps.some(s => s.kind === 'track');
  const seg = snap.segment;
  const pose = seg.step.kind === 'pose' ? seg.step : null;
  const timing = pose ? poseTiming(pose, seg.stepIndex, settings) : null;
  const paused = snap.status === 'paused';

  // Pose change: haptic tick and optional spoken name
  useEffect(() => {
    if (snap.status !== 'running' && snap.status !== 'paused') return;
    if (lastSegment.current === snap.segmentIndex) return;
    lastSegment.current = snap.segmentIndex;
    if (snap.status !== 'running') return;
    navigator.vibrate?.(15);
    if (pose && settings.voice && seg.repeatIndex === 0 && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(new SpeechSynthesisUtterance(pose.nameEn));
    }
  }, [snap.segmentIndex, snap.status, pose, seg.repeatIndex, settings.voice]);

  // Lock-screen controls
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.metadata = new MediaMetadata({
      title: pose ? pose.nameEn : practice.title,
      artist: practice.title,
      album: 'Daily Yoga Companion',
    });
    navigator.mediaSession.setActionHandler('play', () => engine.resume());
    navigator.mediaSession.setActionHandler('pause', () => engine.pause());
    navigator.mediaSession.setActionHandler('previoustrack', () => engine.previous());
    navigator.mediaSession.setActionHandler('nexttrack', () => engine.next());
    return () => {
      (['play', 'pause', 'previoustrack', 'nexttrack'] as const).forEach(a => navigator.mediaSession.setActionHandler(a, null));
    };
  }, [engine, pose, practice.title]);

  const onBackgroundTap = (e: React.MouseEvent) => {
    if (snap.status === 'complete' || snap.status === 'countdown') return;
    if ((e.target as HTMLElement).closest('button, a, input, [data-no-tap]')) return;
    engine.toggle();
  };

  const exit = useCallback(() => {
    endRun();
    onExit();
  }, [onExit]);

  if (run.phase === 'between') return <Between run={run} onExit={exit} />;
  if (run.phase === 'done') return <Complete run={run} onDone={exit} />;

  const poseNames = practice.steps
    .map((s, i) => ({ s, i }))
    .filter((x): x is { s: Extract<typeof x.s, { kind: 'pose' }>; i: number } => x.s.kind === 'pose');

  const jumpTo = (stepIndex: number) => {
    const target = engine
      .getTimeline()
      .segments.find(s => s.round === seg.round && s.stepIndex === stepIndex && s.repeatIndex === 0);
    if (target) engine.jumpToSegment(target.index);
    setList(false);
  };

  const showList = isPoses && wide && panelOpen;
  const sessionPct = Math.min(100, Math.max(0, (snap.position / snap.total) * 100));
  const posePct = Math.min(100, (snap.segmentElapsed / seg.seconds) * 100);

  const headerLines = isPoses
    ? [
        [snap.rounds > 1 ? `Round ${snap.round} of ${snap.rounds}` : null, `Pose ${snap.poseNumber} of ${snap.poseCount}`].filter(Boolean).join('  ·  '),
        seg.repeatTotal > 1 ? `Repeat ${seg.repeatIndex + 1} of ${seg.repeatTotal}` : '',
      ]
    : [practice.free ? `Interval ${seg.index + 1} of ${snap.segmentCount}` : formatLength(snap.total), ''];

  const skip = (d: number) => engine.seek(snap.position + d);

  return (
    <div className={`${styles.player} ${showList ? styles.withPanel : ''}`} onClick={onBackgroundTap}>
      <SunGlow position="center" frozen pulseKey={snap.bellCount} />

      <div className={styles.main}>
        {chained && (
          <p className={styles.routineLine}>
            {run.routine ? `${run.routine.title} · ` : ''}Practice {run.index + 1} of {run.queue.length}: {practice.title}
          </p>
        )}
        <header className={styles.header}>
          <button className={styles.iconBtn} onClick={() => setMore(true)} aria-label="More options" data-no-tap>
            <Icon name="more" />
          </button>
          <p className={styles.headerText}>
            {headerLines[0]}
            {headerLines[1] && <><br />{headerLines[1]}</>}
          </p>
          {isPoses && wide ? (
            <button className={styles.iconBtn} onClick={() => setPanelOpen(o => !o)} aria-label={panelOpen ? 'Hide pose list' : 'Show pose list'}>
              <Icon name="list" />
            </button>
          ) : (
            <span className={styles.iconBtn} />
          )}
        </header>

        <section className={styles.hero} aria-live="polite">
          {practice.infographic ? (
            <div className={`${styles.infoWrap} ${zoomed ? styles.infoZoomed : ''}`}>
              <img src={practice.infographic} alt={`${practice.title} infographic`} className={styles.infographic} onClick={() => setZoomed(z => !z)} decoding="async" />
            </div>
          ) : (
            <div className={styles.names}>
              {pose && settings.showNames ? (
                <>
                  <h1 className={styles.poseName}>{pose.nameSa}</h1>
                  <p className={styles.poseEn}>{pose.nameEn}</p>
                </>
              ) : (
                <h1 className={styles.poseName}>{pose ? ' ' : practice.title}</h1>
              )}
            </div>
          )}
        </section>

        <section className={styles.timeBlock}>
          {snap.status === 'countdown' ? (
            <p className={styles.countdown} aria-live="assertive">
              {snap.countdown}
            </p>
          ) : (
            <p className={styles.timer} aria-label="Time left in this pose">
              {formatClock(isTrack || !isPoses ? snap.total - snap.position : snap.segmentRemaining)}
            </p>
          )}
          {(isTrack || !isPoses) && <p className={styles.sub}>{formatClock(snap.position)} of {formatClock(snap.total)}</p>}
          {paused && <p className={styles.sub}>Paused. Tap anywhere to resume.</p>}
          {snap.status === 'countdown' && <p className={styles.sub}>Get ready</p>}

          {isPoses && (
            <div className={styles.bar} role="progressbar" aria-label="This pose" aria-valuenow={Math.round(posePct)} aria-valuemin={0} aria-valuemax={100}>
              <span style={{ width: `${posePct}%` }} className={styles.barThin} />
            </div>
          )}
          <div className={styles.bar} role="progressbar" aria-label="Session progress" aria-valuenow={Math.round(sessionPct)} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${sessionPct}%` }} />
          </div>
        </section>

        <nav className={styles.controls} aria-label="Playback">
          <button className={styles.iconBtn} onClick={() => (isTrack ? skip(-15) : engine.previous())} aria-label={isTrack ? 'Back 15 seconds' : 'Previous pose'}>
            <Icon name="prev" size={28} />
          </button>
          <button className={styles.play} onClick={() => engine.toggle()} aria-label={paused ? 'Resume' : 'Pause'}>
            <Icon name={paused ? 'play' : 'pause'} size={34} />
          </button>
          <button className={styles.iconBtn} onClick={() => (isTrack ? skip(15) : engine.next())} aria-label={isTrack ? 'Forward 15 seconds' : 'Next pose'}>
            <Icon name="next" size={28} />
          </button>
        </nav>

        {chained && (
          <button className={`text-button ${styles.listLink}`} onClick={skipCurrent} data-no-tap>
            <Icon name="next" size={18} /> {run.index < run.queue.length - 1 ? 'Skip this practice' : 'Finish session'}
          </button>
        )}

        {isPoses && !wide && (
          <button className={`text-button ${styles.listLink}`} onClick={() => setList(true)}>
            <Icon name="list" size={18} /> Pose list
          </button>
        )}
      </div>

      {showList && (
        <aside className={`${styles.panel} surface`} aria-label="Pose list" data-no-tap>
          <PoseList names={poseNames} active={seg.stepIndex} onPick={jumpTo} />
        </aside>
      )}

      {list && (
        <Sheet title="Poses" onClose={() => setList(false)}>
          <PoseList names={poseNames} active={seg.stepIndex} onPick={jumpTo} />
        </Sheet>
      )}

      {more && (
        <Sheet title="More" onClose={() => setMore(false)}>
          <div className={styles.moreBody}>
            {pose && timing && (
              <>
                <div className={styles.moreRow}>
                  <span>This pose</span>
                  <div className={styles.pair}>
                    <button className={styles.pairBtn} onClick={() => engine.adjustPoseSeconds(-5)} aria-label="5 seconds shorter">−5s</button>
                    <span className={styles.pairValue}>{formatClock(timing.seconds)}</span>
                    <button className={styles.pairBtn} onClick={() => engine.adjustPoseSeconds(5)} aria-label="5 seconds longer">+5s</button>
                  </div>
                </div>
                <div className={styles.moreRow}>
                  <span>Repeat this pose</span>
                  <div className={styles.pair}>
                    <button className={styles.pairBtn} onClick={() => engine.adjustPoseRepeat(-1)} aria-label="Repeat fewer times">−</button>
                    <span className={styles.pairValue}>{timing.repeat}×</span>
                    <button className={styles.pairBtn} onClick={() => engine.adjustPoseRepeat(1)} aria-label="Repeat more times">+</button>
                  </div>
                </div>
              </>
            )}
            <label className={styles.sliderLabel} htmlFor="mv">Music volume</label>
            <input id="mv" type="range" min={0} max={100} value={Math.round(settings.musicVolume * 100)} onChange={e => engine.setMusicVolume(Number(e.target.value) / 100)} />
            {!isTrack && (
              <>
                <label className={styles.sliderLabel} htmlFor="bv">Bell volume</label>
                <input id="bv" type="range" min={0} max={100} value={Math.round(settings.bellVolume * 100)} onChange={e => engine.setBellVolume(Number(e.target.value) / 100)} />
              </>
            )}
            {chained && run.index < run.queue.length - 1 && (
              <button
                className="text-button"
                onClick={() => {
                  setMore(false);
                  skipCurrent();
                }}
              >
                Skip to next practice
              </button>
            )}
            <HoldToEnd onEnd={exit} />
          </div>
        </Sheet>
      )}
    </div>
  );
};

const PoseList = ({
  names,
  active,
  onPick,
}: {
  names: { s: { nameEn: string; nameSa: string }; i: number }[];
  active: number;
  onPick: (stepIndex: number) => void;
}) => (
  <ol className={styles.poseList}>
    {names.map(({ s, i }, n) => (
      <li key={i}>
        <button className={styles.poseItem} aria-current={i === active ? 'true' : undefined} onClick={() => onPick(i)}>
          <span className={styles.poseNum}>{n + 1}</span>
          <span>
            <span className={styles.poseItemName}>{s.nameSa}</span>
            <span className={styles.poseItemEn}>{s.nameEn}</span>
          </span>
        </button>
      </li>
    ))}
  </ol>
);

// ---- between practices ----

const BETWEEN_SECONDS = 6;

const Between = ({ run, onExit }: { run: Run; onExit: () => void }) => {
  const next = run.queue[run.index + 1];
  const [left, setLeft] = useState(BETWEEN_SECONDS);

  useEffect(() => {
    if (left <= 0) {
      advanceRun();
      return;
    }
    const t = window.setTimeout(() => setLeft(l => l - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);

  return (
    <div className={styles.complete}>
      <SunGlow position="center" />
      <div className={styles.completeBody}>
        <p className="eyebrow-soft">
          {run.routine ? run.routine.title : 'Session'} · Practice {run.index + 1} of {run.queue.length} done
        </p>
        <h1 className={styles.completeTitle}>Up next</h1>
        <p className={styles.nextTitle}>{next.title}</p>
        <p className="eyebrow-soft">{next.note ?? next.blurb}</p>
        <p className={styles.betweenCount} aria-live="polite">
          Starting in {left}
        </p>
        <button className="pill-button" onClick={() => advanceRun()}>
          Start now
        </button>
        <button className="text-button" onClick={onExit}>
          Stop session
        </button>
      </div>
    </div>
  );
};

// ---- completion ----

const Complete = ({ run, onDone }: { run: Run; onDone: () => void }) => {
  const practice = run.queue[run.index];
  const snap = run.engine.getSnapshot();
  const streak = currentStreak(loadHistory());
  const verse = dailyVerse();
  const chained = run.queue.length > 1;
  const totalSeconds = run.completed.reduce((n, c) => n + c.seconds, 0);
  const rounds = !chained && practice.roundsAdjustable ? snap.rounds : 0;

  return (
    <div className={styles.complete}>
      <SunGlow position="center" bloom />
      <div className={styles.completeBody}>
        <h1 className={styles.completeTitle}>{chained ? 'Session complete' : 'Practice complete'}</h1>
        <p className={styles.completeStats}>
          {chained
            ? `${run.routine?.title ?? 'Session'}, ${run.completed.length} practices, ${formatLength(totalSeconds)}`
            : `${practice.title}, ${formatLength(snap.total)}${rounds > 0 ? `, ${rounds} ${rounds === 1 ? 'round' : 'rounds'}` : ''}`}
        </p>
        {streak > 0 && <p className={styles.completeStreak}>Day {streak}</p>}

        <blockquote className={`${styles.verse} surface`}>
          <p lang="sa" className={styles.verseSa}>{verse.sanskrit}</p>
          <p className={styles.verseEn}>{verse.english}</p>
        </blockquote>

        <button className="pill-button" onClick={onDone}>
          Done
        </button>
        <button className="text-button" onClick={() => downloadReminder(chained ? run.routine?.title ?? 'Session' : practice.title, chained ? totalSeconds : snap.total)}>
          Remind me tomorrow at this time
        </button>
      </div>
    </div>
  );
};

// ---- route entry ----

const useRun = () => useSyncExternalStore(subscribeRun, getRun);

interface PlayerProps {
  /** A single practice (/play/:id) ... */
  practice?: Practice;
  /** ... or a session of several (/session/:id). */
  routine?: Routine;
  onExit: () => void;
  onEdit?: () => void;
}

export const Player = ({ practice, routine, onExit, onEdit }: PlayerProps) => {
  const run = useRun();
  const matches = run && (routine ? run.routine?.id === routine.id : !run.routine && run.queue.length === 1 && run.queue[0].id === practice?.id);

  useWakeLock(Boolean(matches));

  if (matches && run) return <Running run={run} onExit={onExit} />;

  if (routine) {
    const queue = routine.items.map(getPractice).filter((p): p is Practice => Boolean(p));
    return (
      <>
        <SunGlow position="center" />
        <main className={`screen ${styles.preview}`}>
          <button className={styles.iconBtn} onClick={onExit} aria-label="Back">
            <Icon name="back" />
          </button>
          <RoutineCard routine={routine} onBegin={() => queue.length && beginRun(queue, routine)} onEdit={onEdit} />
        </main>
      </>
    );
  }

  if (!practice) return null;

  return (
    <>
      <SunGlow position="center" />
      <main className={`screen ${styles.preview}`}>
        <button className={styles.iconBtn} onClick={onExit} aria-label="Back">
          <Icon name="back" />
        </button>
        <Icon name={practice.icon} size={36} />
        <h1 className={styles.previewTitle}>{practice.title}</h1>
        <p className="eyebrow-soft">{practice.blurb}</p>
        <StartPanel practice={practice} onBegin={() => beginRun([practice])} />
      </main>
    </>
  );
};
